// Placement maths (05e-1) — main.js's grid rules, pure so they're unit-testable: snap to
// the grid, "is this spot free?", the first empty slot, and the sums behind growing /
// shrinking / shifting the canvas. canvas-space.ts feeds them the live page.
//
// Tuned by hand in the prototype (the 10-block reach, the 8-square edge room, the Notes
// exemption) — copied exactly, not "improved".

import { GRID_GAP, GRID_SIZE } from "./module-shell.js";

/** One grid cell plus its gap, in px. */
export const STEP = GRID_SIZE + GRID_GAP;
/** How far (px) every module moves while a drag holds the canvas open — 10 grid spaces of
 *  room in each direction. */
export const DRAG_EXPANSION = 1100;
/** How far in (px) a drop may land before the canvas shifts to make room. */
export const DRAG_PADDING = 0;
/** Empty grid squares always kept right of / below the module cluster, so you never end
 *  up pushed against the canvas edge. */
export const EDGE_PADDING = 8;
/** No module may land more than this many blocks outside the cluster's box. */
export const MAX_REACH = 10;

/** A module's grid footprint. */
export interface Footprint {
  col: number;
  row: number;
  cols: number;
  rows: number;
}

/** main.js getSnapCoords: the nearest grid cell to a px position. */
export function snapCoords(px: number, py: number): { x: number; y: number; col: number; row: number } {
  const col = Math.round(px / STEP);
  const row = Math.round(py / STEP);
  return { x: col * STEP, y: row * STEP, col, row };
}

/** main.js moduleFootprint: a box in px → grid cells (width / height include no trailing
 *  gap, so a gap is added back before dividing). */
export function footprintOf(left: number, top: number, width: number, height: number): Footprint {
  return {
    col: Math.round(left / STEP),
    row: Math.round(top / STEP),
    cols: Math.round((width + GRID_GAP) / STEP),
    rows: Math.round((height + GRID_GAP) / STEP),
  };
}

export function footprintsOverlap(a: Footprint, b: Footprint): boolean {
  return a.col < b.col + b.cols && a.col + a.cols > b.col && a.row < b.row + b.rows && a.row + a.rows > b.row;
}

export interface CanvasBounds {
  /** The canvas's current size in grid cells (main.js GRID_COLS / workspaceGridRows). */
  gridCols: number;
  gridRows: number;
  /** A drag is holding the canvas open (DRAG_EXPANSION) — reach grows by that much. */
  expanded: boolean;
}

/**
 * main.js isAreaClear: can a module of this footprint land here? `others` are every
 * module except the ones being placed. False when it's off the top / left, overlaps
 * another module (unless Notes — it may sit on anything), runs past the canvas edge
 * (which grows with the cluster: EDGE_PADDING beyond its right / bottom), or lands more
 * than MAX_REACH blocks outside the cluster's box.
 */
export function isAreaClear(
  target: Footprint,
  others: readonly Footprint[],
  bounds: CanvasBounds,
  notesExempt: boolean,
): boolean {
  if (target.col < 0 || target.row < 0) return false;

  let minCol = Infinity;
  let minRow = Infinity;
  let maxCol = -Infinity;
  let maxRow = -Infinity;
  for (const m of others) {
    if (!notesExempt && footprintsOverlap(target, m)) return false;
    minCol = Math.min(minCol, m.col);
    minRow = Math.min(minRow, m.row);
    maxCol = Math.max(maxCol, m.col + m.cols);
    maxRow = Math.max(maxRow, m.row + m.rows);
  }

  // The edge room is only added once there's a real cluster, so a bare canvas isn't
  // padded past its own size.
  const effectiveCols = Math.max(bounds.gridCols, maxCol === -Infinity ? maxCol : maxCol + EDGE_PADDING);
  const effectiveRows = Math.max(bounds.gridRows, maxRow === -Infinity ? maxRow : maxRow + EDGE_PADDING);
  // While a drag holds the canvas open, the indicator can reach 10 new blocks past that.
  const buffer = bounds.expanded ? Math.ceil(DRAG_EXPANSION / STEP) + 2 : 0;
  if (target.col + target.cols > effectiveCols + buffer) return false;
  if (target.row + target.rows > effectiveRows + buffer) return false;

  // The limit that governs in practice; the edge check above is a safety net.
  if (minCol !== Infinity) {
    const dCol = Math.max(0, minCol - (target.col + target.cols), target.col - maxCol);
    const dRow = Math.max(0, minRow - (target.row + target.rows), target.row - maxRow);
    if (dCol > MAX_REACH || dRow > MAX_REACH) return false;
  }
  return true;
}

/** main.js findOverlappingModule: the first module in the way (drag-to-merge needs to
 *  know WHICH), or -1. */
export function firstOverlap(target: Footprint, others: readonly Footprint[]): number {
  return others.findIndex((m) => footprintsOverlap(target, m));
}

/** The canvas's minimum height in rows: the viewport, plus two rows of slack. */
export function minGridRows(innerHeight: number): number {
  return Math.max(6, Math.ceil((innerHeight - 166) / STEP) + 2);
}

/** The canvas's starting width in columns: the viewport, plus two columns of slack. */
export function initialGridCols(innerWidth: number): number {
  return Math.max(8, Math.ceil((innerWidth - 72) / STEP) + 2);
}

/** main.js setWorkspaceGridRows: never below the minimum; `|| min` as main.js (a bad
 *  count falls back to the minimum). */
export function clampGridRows(rowCount: number, minRows: number): number {
  return Math.max(minRows, Math.ceil(Number(rowCount) || minRows));
}

/**
 * main.js getFirstEmptySlot: the first free cell, row by row, that also stays clear of
 * the welcome text (desktop). Not found → add rows (at least 12) and look again, three
 * times, then give up at 0,0. `gridRows` comes back grown when it had to add rows.
 */
export function firstEmptySlot(
  cols: number,
  rows: number,
  opts: {
    gridCols: number;
    gridRows: number;
    minRows: number;
    isClear: (target: Footprint, gridRows: number) => boolean;
    /** The welcome text's cells, or null on mobile (no welcome text to dodge). */
    welcome: Footprint | null;
  },
): { col: number; row: number; gridRows: number } {
  const wantCols = Math.max(1, Math.min(opts.gridCols, Number(cols) || 1));
  const wantRows = Math.max(1, Number(rows) || 1);
  let gridRows = opts.gridRows;
  for (let pass = 0; pass < 3; pass++) {
    for (let row = 0; row <= gridRows - wantRows; row++) {
      for (let col = 0; col <= opts.gridCols - wantCols; col++) {
        const target = { col, row, cols: wantCols, rows: wantRows };
        if (!opts.isClear(target, gridRows)) continue;
        if (!opts.welcome || !footprintsOverlap(target, opts.welcome)) return { col, row, gridRows };
      }
    }
    gridRows = clampGridRows(gridRows + Math.max(wantRows, 12), opts.minRows);
  }
  return { col: 0, row: 0, gridRows };
}

/** main.js collapseWorkspace's shift: a drop that landed left of / above the canvas's
 *  origin shifts every module right / down by that much (a cancelled drag shifts none). */
export function dropShift(snapX: number, snapY: number, isCancel: boolean): { x: number; y: number } {
  if (isCancel) return { x: 0, y: 0 };
  return {
    x: Math.max(0, DRAG_PADDING - (snapX - DRAG_EXPANSION)),
    y: Math.max(0, DRAG_PADDING - (snapY - DRAG_EXPANSION)),
  };
}

/** main.js trimWorkspaceShift: how much of the built-up shift can come back off now that
 *  the space it made (left of / above the modules) is empty again — never more than was
 *  added. */
export function trimAmount(shift: { x: number; y: number }, minLeft: number, minTop: number): { x: number; y: number } {
  return {
    x: Math.min(shift.x, Math.max(0, minLeft - DRAG_PADDING)),
    y: Math.min(shift.y, Math.max(0, minTop - DRAG_PADDING)),
  };
}

/** main.js ensureCanvasPadding: the canvas size that keeps EDGE_PADDING squares right of /
 *  below the cluster. Only ever grows. */
export function paddedGrid(footprints: readonly Footprint[], gridCols: number, gridRows: number): { gridCols: number; gridRows: number } {
  let maxCol = 0;
  let maxRow = 0;
  for (const f of footprints) {
    maxCol = Math.max(maxCol, f.col + f.cols);
    maxRow = Math.max(maxRow, f.row + f.rows);
  }
  return {
    gridCols: maxCol + EDGE_PADDING > gridCols ? maxCol + EDGE_PADDING : gridCols,
    gridRows: maxRow + EDGE_PADDING > gridRows ? maxRow + EDGE_PADDING : gridRows,
  };
}

/**
 * main.js scrollToTopLeft's pick: which modules make up the top-left of the cluster. A
 * median guard ignores a lone module dragged far away (more than 10 blocks out). Returns
 * their indexes, or every index if none qualify.
 */
export function topLeftCluster(boxes: readonly { left: number; top: number }[]): number[] {
  if (!boxes.length) return [];
  const lefts = boxes.map((b) => b.left).sort((a, b) => a - b);
  const tops = boxes.map((b) => b.top).sort((a, b) => a - b);
  const medLeft = lefts[Math.floor(lefts.length / 2)]!;
  const medTop = tops[Math.floor(tops.length / 2)]!;
  const targetLeft = Math.max(lefts[0]!, medLeft - 10 * STEP);
  const targetTop = Math.max(tops[0]!, medTop - 10 * STEP);
  const inCluster = boxes.flatMap((b, i) => (b.left <= targetLeft + STEP && b.top <= targetTop + STEP ? [i] : []));
  return inCluster.length ? inCluster : boxes.map((_, i) => i);
}
